import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const Agents = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        average_rating: 0,
        top_rated: 0,
    });

    // Form states
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        email: '',
        mobile: '',
        password: '',
        confirmPassword: '',
        status: true,
        rating: 0,
    });
    const [formErrors, setFormErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Fetch agents when filters change
    useEffect(() => {
        fetchAgents(1);
    }, [debouncedSearch, filterStatus]);

    // Initial fetch
    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const params = {
                page: pageNumber,
                searchTerm: debouncedSearch || undefined,
                status: filterStatus !== 'all' ? filterStatus : undefined,
            };

            const response = await api.get('/organizer/agents', { params });
            const paginator = response.data.data;
            const statsFromApi = response.data.stats;

            setAgents(paginator.data || []);
            setPagination(paginator);
            setPage(pageNumber);
            setStats(statsFromApi || stats);
        } catch (error) {
            console.error('Error fetching agents:', error);
            toast.error('Failed to load agents');
            setAgents([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

        if (!formData.mobile.trim()) newErrors.mobile = 'Mobile is required';

        if (formMode === 'create' && !formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password && formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (formMode === 'create' && formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (formData.rating < 0 || formData.rating > 5) {
            newErrors.rating = 'Rating must be between 0 and 5';
        }

        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmitForm = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setFormLoading(true);

            const submitData = {
                name: formData.name,
                email: formData.email,
                mobile: formData.mobile,
                status: formData.status,
                rating: formData.rating,
            };

            // Only include password if provided (for create) or if changed (for edit)
            if (formMode === 'create' || formData.password) {
                submitData.password = formData.password;
            }

            if (formMode === 'edit') {
                await api.put(`/organizer/agents/${formData.id}`, submitData);
                toast.success('Agent updated successfully');
            } else {
                await api.post('/organizer/agents', submitData);
                toast.success('Agent created successfully');
            }

            // Reset form and refresh agents list
            resetForm();
            fetchAgents();

        } catch (error) {
            if (error.response?.data?.errors) {
                setFormErrors(error.response.data.errors);
            } else {
                toast.error(error.response?.data?.message || 'Failed to save agent');
            }
        } finally {
            setFormLoading(false);
        }
    };

    const openAssignModal = async (agent) => {
        try {
            const res = await api.get(`/organizer/agents/${agent.id}/assignable-events`);
            const events = res.data.data;

            if (events.length === 0) {
                return Swal.fire({
                    title: 'No Events Available',
                    html: `
                    <div class="text-center py-3">
                        <i class="fas fa-calendar-times fa-3x text-purple-500 mb-3"></i>
                        <p class="mb-0">There are no events available to assign this agent to.</p>
                    </div>
                `,
                    icon: 'info',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#f6b024',
                    customClass: {
                        confirmButton: 'btn !bg-purple-500 !text-white'
                    }
                });
            }

            const { value: formValues } = await Swal.fire({
                title: `<div class="text-purple-500"> Assign Agent</div>`,
                html: `
                <div class="text-left">
                    <div class="alert border-left-yellow mb-3">
                        <div class="d-flex">
                            <i class="fas fa-user-circle fa-2x mr-3 text-purple-500"></i>
                            <div>
                                <h6 class="font-weight-bold mb-1">${agent.name}</h6>
                                <p class="mb-0 text-muted small">${agent.email}</p>
                            </div>
                        </div>
                    </div>
                    
                    <p class="mb-3">Select an event to assign this agent to:</p>
                    
                    <div class="form-group">
                        <label class="font-weight-bold text-gray-700 mb-2">Event</label>
                        <select id="swal-event" class="form-control select-event">
                            <option value="" disabled selected>-- Select Event --</option>
                            ${events.map(e => `
                                <option value="${e.id}">
                                    ${e.title} 
                                    <span class="text-muted">• ${new Date(e.event_start).toLocaleDateString()}</span>
                                </option>
                            `).join('')}
                        </select>
                    </div>
                    
                    <div class="mt-3 p-3 bg-light rounded">
                        <i class="fas fa-info-circle text-purple-500 mr-2"></i>
                        <small class="text-muted">
                            Agent will be able to manage check-ins, check-outs, and event operations.
                        </small>
                    </div>
                </div>
            `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: '<i class="fas fa-check mr-2"></i> Assign',
                cancelButtonText: '<i class="fas fa-times mr-2"></i> Cancel',
                confirmButtonColor: '#f6b024',
                cancelButtonColor: '#6c757d',
                customClass: {
                    confirmButton: 'btn !bg-purple-500 !text-white !font-weight-bold',
                    cancelButton: 'btn btn-outline-danger',
                    popup: 'rounded-lg'
                },
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    const select = document.getElementById('swal-event');
                    if (!select.value) {
                        Swal.showValidationMessage('Please select an event');
                        return false;
                    }
                    return { eventId: select.value };
                },
                allowOutsideClick: () => !Swal.isLoading()
            });

            if (formValues && formValues.eventId) {
                await api.post(`/organizer/agents/${agent.id}/assign`, { event_id: formValues.eventId });

                toast.success(`Agent "${agent.name}" assigned successfully!`, {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });

                fetchAgents();
            }
        } catch (error) {
            console.error('Error assigning agent:', error);
            toast.error(error.response?.data?.message || 'Failed to assign agent', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const openUnassignModal = async (agent) => {
        try {
            const res = await api.get(`/organizer/agents/${agent.id}/assigned-events`);
            const events = res.data.data;

            if (events.length === 0) {
                return Swal.fire({
                    title: 'No Assigned Events',
                    html: `
                <div class="text-center py-4">
                    <i class="fas fa-user-check fa-3x text-purple-500 mb-3"></i>
                    <h5 class="font-weight-bold mb-2">${agent.name}</h5>
                    <p class="text-muted">This agent is not currently assigned to any events.</p>
                </div>
            `,
                    icon: 'info',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#f6b024',
                    customClass: {
                        confirmButton: 'btn !bg-purple-500 !text-white'
                    }
                });
            }

            // Generate HTML for assigned events list
            const eventsListHTML = events.map(event => `
            <div class="assigned-event-item mb-2 p-2 border rounded bg-light">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <h6 class="font-weight-bold mb-0">${event.title}</h6>
                        <small class="text-muted">
                            <i class="fas fa-calendar-alt mr-1"></i>
                            ${new Date(event.event_start).toLocaleDateString()} 
                            ${event.event_start_time ? `• ${event.event_start_time}` : ''}
                        </small>
                    </div>
                    <div>
                        <span class="badge badge-outline-secondary">
                            <i class="fas fa-map-marker-alt mr-1"></i>
                            ${event.location || 'No location'}
                        </span>
                    </div>
                </div>
            </div>
        `).join('');

            const { value: formValues } = await Swal.fire({
                title: `<div class="text-danger"> Unassign Agent</div>`,
                html: `
            <div class="text-left">
             
                
                <!-- Agent Info -->
                <div class="mb-4">
                    <div class="d-flex align-items-center">
                        <div class="mr-3">
                            <img src="https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name)}&background=f6b024&color=fff&size=48" 
                                 class="rounded-circle" alt="${agent.name}">
                        </div>
                        <div>
                            <h6 class="font-weight-bold mb-0">${agent.name}</h6>
                            <p class="text-muted small mb-0">${agent.email}</p>
                            <p class="text-muted small mb-0">Mobile: ${agent.mobile}</p>
                        </div>
                    </div>
                </div>
                
                <!-- Assigned Events List -->
                <div class="mb-4">
                    <h6 class="font-weight-bold text-gray-700 mb-3">
                        <i class="fas fa-list-check mr-2 text-purple-500"></i>
                        Currently Assigned Events (${events.length})
                    </h6>
                    <div class="assigned-events-container max-h-64 overflow-y-auto pr-2">
                        ${eventsListHTML}
                    </div>
                </div>
                
                <!-- Event Selection -->
                <div class="form-group">
                    <label class="font-weight-bold text-gray-700 mb-2">
                        <i class="fas fa-hand-pointer mr-2 text-purple-500"></i>
                        Select Event to Unassign From
                    </label>
                    <select id="swal-event-unassign" class="form-control select-event">
                        <option value="" disabled selected>-- Select an event --</option>
                        ${events.map(e => `
                            <option value="${e.id}">
                                ${e.title} 
                                <span class="text-muted">• ${new Date(e.event_start).toLocaleDateString()}</span>
                            </option>
                        `).join('')}
                    </select>
                </div>
                
                <!-- Status Info -->
            </div>
        `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: '<i class="fas fa-check mr-2"></i> Confirm Unassign',
                cancelButtonText: '<i class="fas fa-times mr-2"></i> Cancel',
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                width: '650px',
                customClass: {
                    confirmButton: 'btn !bg-purple-500 !text-white !font-bold !font-weight-bold',
                    cancelButton: 'btn btn-outline-danger',
                    popup: 'rounded-lg',
                    container: 'z-9999'
                },
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    const select = document.getElementById('swal-event-unassign');
                    if (!select.value) {
                        Swal.showValidationMessage('Please select an event to unassign');
                        return false;
                    }

                    // Get selected event details for confirmation
                    const selectedEvent = events.find(e => e.id == select.value);
                    return {
                        eventId: select.value,
                        eventTitle: selectedEvent?.title || 'Unknown Event'
                    };
                },
                allowOutsideClick: () => !Swal.isLoading(),
                didOpen: () => {
                    // Add custom scrollbar styling
                    const container = document.querySelector('.assigned-events-container');
                    if (container) {
                        container.style.maxHeight = '200px';
                        container.style.scrollbarWidth = 'thin';
                    }
                }
            });

            if (formValues && formValues.eventId) {
                // Enhanced confirmation dialog
                const confirmation = await Swal.fire({
                    title: 'Confirm Unassignment',
                    html: `
                <div class="text-center py-3">
                    <i class="fas fa-user-minus fa-3x text-danger mb-3"></i>
                    <h5 class="font-weight-bold mb-2">Are you sure?</h5>
                    
                    <div class="alert alert-warning text-left mb-3">
                        <div class="d-flex">
                            <i class="fas fa-exclamation-circle fa-lg mt-1 mr-3"></i>
                            <div>
                                <p class="mb-1 font-weight-bold">You are about to unassign:</p>
                                <p class="mb-0">
                                    <strong>${agent.name}</strong> 
                                    <span class="text-muted">from</span> 
                                    <strong class="text-danger">"${formValues.eventTitle}"</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="text-left bg-light p-3 rounded">
                        <p class="mb-1 small"><i class="fas fa-times-circle text-danger mr-2"></i>Agent will lose event access</p>
                        <p class="mb-1 small"><i class="fas fa-ban text-danger mr-2"></i>Cannot manage check-ins/check-outs</p>
                        <p class="mb-0 small"><i class="fas fa-history text-danger mr-2"></i>This action cannot be undone</p>
                    </div>
                </div>
            `,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '<i class="fas fa-check mr-2"></i> Yes, Unassign Agent',
                    cancelButtonText: '<i class="fas fa-times mr-2"></i> No, Keep Assigned',
                    confirmButtonColor: '#dc3545',
                    cancelButtonColor: '#6c757d',
                    reverseButtons: true,
                    width: '550px',
                    customClass: {
                        confirmButton: 'btn btn-danger !font-weight-bold',
                        cancelButton: 'btn btn-outline-secondary'
                    }
                });

                if (confirmation.isConfirmed) {
                    try {
                        await api.post(`/organizer/agents/${agent.id}/unassign`, { event_id: formValues.eventId });

                        toast.success(`Agent "${agent.name}" unassigned from "${formValues.eventTitle}" successfully!`, {
                            position: "top-right",
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                        });

                        fetchAgents();
                    } catch (error) {
                        console.error('Error unassigning agent:', error);
                        toast.error(error.response?.data?.message || 'Failed to unassign agent', {
                            position: "top-right",
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Error loading assigned events:', error);
            toast.error('Failed to load assigned events', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };



    const resetForm = () => {
        setFormData({
            id: null,
            name: '',
            email: '',
            mobile: '',
            password: '',
            confirmPassword: '',
            status: true,
            rating: 0,
        });
        setFormErrors({});
        setShowForm(false);
        setFormMode('create');
    };

    const handleEdit = (agent) => {
        setFormMode('edit');
        setFormData({
            id: agent.id,
            name: agent.name,
            email: agent.email,
            mobile: agent.mobile,
            password: '',
            confirmPassword: '',
            status: agent.status,
            rating: agent.rating || 0,
        });
        setFormErrors({});
        setShowForm(true);
    };

    const handleCreate = () => {
        setFormMode('create');
        resetForm();
        setFormData(prev => ({ ...prev, status: true }));
        setShowForm(true);
    };

    const handleToggleStatus = async (agentId, currentStatus, agentName) => {
        const action = currentStatus ? 'Disable' : 'Enable';
        const result = await Swal.fire({
            title: `${action} Agent`,
            text: `Are you sure you want to ${action.toLowerCase()} "${agentName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.put(`/organizer/agents/${agentId}/toggle-status`);
            toast.success(`Agent ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
            fetchAgents();
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update agent status');
        }
    };

    const handleDelete = async (agentId, agentName) => {
        const result = await Swal.fire({
            title: 'Delete Agent',
            text: `Are you sure you want to delete "${agentName}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/organizer/agents/${agentId}`);
            toast.success('Agent deleted successfully');
            fetchAgents();
        } catch (error) {
            console.error('Error deleting agent:', error);
            toast.error('Failed to delete agent');
        }
    };

    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3;

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button className="page-link text-gray-900" onClick={() => fetchAgents(current - 1)}>
                            Previous
                        </button>
                    </li>

                    {start > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link text-gray-900" onClick={() => fetchAgents(1)}>
                                    1
                                </button>
                            </li>
                            {start > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}

                    {pageNumbers.map(num => (
                        <li key={num} className={`page-item ${num === current ? 'active' : ''}`}>
                            <button
                                className={`page-link text-gray-900 ${
                                    num === current
                                        ? '!bg-purple-500 !text-white !border-purple-500 border'
                                        : ''
                                }`}
                                onClick={() => fetchAgents(num)}
                            >
                                {num}
                            </button>
                        </li>
                    ))}

                    {end < last && (
                        <>
                            {end < last - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button className="page-link text-gray-900" onClick={() => fetchAgents(last)}>
                                    {last}
                                </button>
                            </li>
                        </>
                    )}

                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button className="page-link text-gray-900" onClick={() => fetchAgents(current + 1)}>
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    const renderRatingStars = (rating) => {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        return (
            <div className="d-flex align-items-center">
                {[...Array(fullStars)].map((_, i) => (
                    <i key={`full-${i}`} className="fas fa-star text-warning mr-1"></i>
                ))}
                {hasHalfStar && <i className="fas fa-star-half-alt text-warning mr-1"></i>}
                {[...Array(emptyStars)].map((_, i) => (
                    <i key={`empty-${i}`} className="far fa-star text-warning mr-1"></i>
                ))}
                <span className="ml-1 small">({rating.toFixed(1)})</span>
            </div>
        );
    };

    return (
        <div className="container-fluid mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between text-gray-900 font-weight-bold align-items-center mb-4">
                <h5 className="text-gray-900 font-weight-bold">Agents Management</h5>
                {!showForm && (
                    <button
                        onClick={handleCreate}
                        className="btn !text-white !font-weight-bold !bg-purple-500 shadow-sm hover:!bg-purple-600 flex items-center gap-2"
                    >
                        <span className="text-xl mr-2">+</span>
                        Add New Agent
                    </button>
                )}
            </div>

            {/* Agent Form (when showForm is true) */}
            {showForm && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    {formMode === 'edit' ? 'Edit Agent' : 'Add New Agent'}
                                </h5>
                                <button
                                    onClick={resetForm}
                                    className="btn btn-sm btn-outline-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmitForm}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="name">Name *</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleFormChange}
                                                    placeholder="Enter agent name"
                                                />
                                                {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="email">Email *</label>
                                                <input
                                                    type="email"
                                                    className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleFormChange}
                                                    placeholder="Enter agent email"
                                                    disabled={formMode === 'edit'}
                                                />
                                                {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="mobile">Mobile *</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${formErrors.mobile ? 'is-invalid' : ''}`}
                                                    id="mobile"
                                                    name="mobile"
                                                    value={formData.mobile}
                                                    onChange={handleFormChange}
                                                    placeholder="Enter mobile number"
                                                />
                                                {formErrors.mobile && <div className="invalid-feedback">{formErrors.mobile}</div>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="rating">Rating (0-5)</label>
                                                <input
                                                    type="number"
                                                    className={`form-control ${formErrors.rating ? 'is-invalid' : ''}`}
                                                    id="rating"
                                                    name="rating"
                                                    value={formData.rating}
                                                    onChange={handleFormChange}
                                                    min="0"
                                                    max="5"
                                                    step="0.1"
                                                    placeholder="Enter rating"
                                                />
                                                {formErrors.rating && <div className="invalid-feedback">{formErrors.rating}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="password">
                                                    {formMode === 'edit' ? 'New Password (leave blank to keep current)' : 'Password *'}
                                                </label>
                                                <input
                                                    type="password"
                                                    className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                                                    id="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleFormChange}
                                                    placeholder="Enter password"
                                                />
                                                {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                                                <small className="form-text text-muted">Minimum 8 characters</small>
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label htmlFor="confirmPassword">
                                                    {formMode === 'edit' ? 'Confirm New Password' : 'Confirm Password *'}
                                                </label>
                                                <input
                                                    type="password"
                                                    className={`form-control ${formErrors.confirmPassword ? 'is-invalid' : ''}`}
                                                    id="confirmPassword"
                                                    name="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={handleFormChange}
                                                    placeholder="Confirm password"
                                                />
                                                {formErrors.confirmPassword && <div className="invalid-feedback">{formErrors.confirmPassword}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group mt-4">
                                        <button
                                            type="submit"
                                            className="btn p-3 !bg-purple-500 !text-white !font-bold w-100 hover:!bg-purple-600"
                                            disabled={formLoading}
                                        >
                                            {formLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                    {formMode === 'edit' ? 'Updating...' : 'Creating...'}
                                                </>
                                            ) : (
                                                <>{formMode === 'edit' ? 'Update Agent' : 'Create Agent'}</>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics Cards (only show when not in form mode) */}
            {!showForm && (
                <div className="row mb-4">
                    {/* Total Agents Card */}
                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card border-left-yellow shadow h-100 py-2">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                            Total Agents
                                        </div>
                                        <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.total}</div>
                                    </div>
                                    <div className="col-auto">
                                        <i className="fas fa-users fa-2x text-gray-300"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Active Agents Card */}
                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card border-left-yellow shadow h-100 py-2">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                            Active Agents
                                        </div>
                                        <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.active}</div>
                                    </div>
                                    <div className="col-auto">
                                        <i className="fas fa-user-check fa-2x text-gray-300"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Average Rating Card */}
                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card border-left-yellow shadow h-100 py-2">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                            Average Rating
                                        </div>
                                        <div className="h5 mb-0 font-weight-bold text-gray-800">
                                            {stats.average_rating ? parseFloat(stats.average_rating).toFixed(1) : '0.0'}
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <i className="fas fa-star fa-2x text-gray-300"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Rated Agents Card */}
                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card border-left-yellow shadow h-100 py-2">
                            <div className="card-body">
                                <div className="row no-gutters align-items-center">
                                    <div className="col mr-2">
                                        <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                            Top Rated Agents
                                        </div>
                                        <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.top_rated || 0}</div>
                                    </div>
                                    <div className="col-auto">
                                        <i className="fas fa-crown fa-2x text-gray-300"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters (only show when not in form mode) */}
            {!showForm && (
                <div className="bg-white rounded-lg shadow p-6 mb-4 border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Search */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, email, or mobile..."
                                className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
                            />
                        </div>

                        {/* Status Filter */}

                        {/* Clear Filters */}
                        <div className="flex items-end">
                            {(searchTerm || filterStatus !== 'all') && (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilterStatus('all');
                                    }}
                                    className="btn btn-outline-danger w-full"
                                >
                                    <i className="fas fa-redo mr-2"></i>
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Agents List (only show when not in form mode) */}
            {!showForm && (
                <div className="card">
                    <div className="card-body">
                        <h5 className="card-title mb-4">Agents List</h5>

                        {loading ? (
                            <div className="text-center py-12">
                                <svg
                                    className="animate-spin h-12 w-12 mx-auto text-purple-500"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                <p className="mt-4 text-gray-600">Loading agents...</p>
                            </div>
                        ) : agents.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="text-6xl mb-4">👥</div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                    {searchTerm || filterStatus !== 'all' ? 'No agents found' : 'No agents yet'}
                                </h3>
                                <p className="text-gray-600 mb-6">
                                    {searchTerm || filterStatus !== 'all'
                                        ? 'Try adjusting your filters'
                                        : 'Add your first agent to get started'}
                                </p>
                                {!searchTerm && filterStatus === 'all' && (
                                    <button
                                        onClick={handleCreate}
                                        className="btn !bg-purple-500 !text-white"
                                    >
                                        Add Your First Agent
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Mobile</th>
                                            <th>Rating</th>
                                            <th>Created At</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {agents.map((agent) => (
                                            <tr key={agent.id}>
                                                <td className="font-weight-bold text-purple-500">#{agent.id}</td>
                                                <td>
                                                    <div className="font-weight-bold text-gray-800">{agent.name}</div>
                                                </td>
                                                <td>
                                                    <div className="text-gray-600">{agent.email}</div>
                                                </td>
                                                <td>
                                                    <div className="text-gray-600">{agent.mobile}</div>
                                                </td>
                                                <td>{renderRatingStars(parseFloat(agent.rating || 0))}</td>
                                                <td>
                                                    <small className="text-muted">
                                                        {new Date(agent.created_at).toLocaleDateString()}
                                                    </small>
                                                </td>
                                                <td className="text-center">
                                                    <div className="btn-group" role="group">
                                                        <button
                                                            onClick={() => handleEdit(agent)}
                                                            className="btn btn-sm btn-outline-primary"
                                                            title="Edit Agent"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => openAssignModal(agent)}
                                                            className="btn btn-sm btn-outline-success"
                                                        >
                                                            <i className="fas fa-link"></i>
                                                        </button>

                                                        <button
                                                            onClick={() => openUnassignModal(agent)}
                                                            className="btn btn-sm btn-outline-danger"
                                                        >
                                                            <i className="fas fa-close"></i>
                                                        </button>

                                                        <button
                                                            onClick={() => handleDelete(agent.id, agent.name)}
                                                            className="btn btn-sm btn-outline-danger"
                                                            title="Delete Agent"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                                {renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Agents;